import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { LoadingState } from '@/components/workflow/LoadingState';
import { CurrentUserProvider } from '@/components/auth/CurrentUserProvider';
import { useAuthStore } from '@/stores/auth.store';
import { roleHomePath } from '@/lib/roleHome';

// Auth Guards
import { AuthGuard } from '@/components/guards/AuthGuard';
import { PatientGuard, PractitionerGuard, SecretaryGuard, AdminGuard, RoleGuard } from '@/components/guards/RoleGuard';
import { BookingProvider } from '@/routes/patient/booking/BookingProvider';
const LoginPage = lazy(() => import('@/routes/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/routes/auth/RegisterPage'));
const EmailVerificationPage = lazy(() => import('@/routes/auth/EmailVerificationPage'));
const ForgotPasswordPage = lazy(() => import('@/routes/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/routes/auth/ResetPasswordPage'));
const AdminPractitionersPage = lazy(() =>
  import('@/features/admin/practitioners/AdminPractitionersPage').then(m => ({
    default: m.AdminPractitionersPage,
  }))
);

const AdminAuditPage = lazy(() =>
  import('@/features/admin/audit/AdminAuditPage').then(m => ({
    default: m.AdminAuditPage,
  }))
);

const AdminSupportPage = lazy(() =>
  import('@/features/admin/support/AdminSupportPage').then(m => ({
    default: m.AdminSupportPage,
  }))
);

const AdminDashboardPage = lazy(() =>
  import('@/features/admin/dashboard/AdminDashboardPage').then(m => ({
    default: m.AdminDashboardPage,
  }))
);

const SearchPage = lazy(() => import('@/routes/patient/search/SearchPage'));
const DoctorDetailPage = lazy(() => import('@/routes/patient/search/DoctorDetailPage'));
const BookingPage = lazy(() => import('@/routes/patient/booking/BookingPage'));
const WaitlistPage = lazy(() => import('@/routes/patient/waitlist/WaitlistPage'));
const AppointmentsPage = lazy(() => import('@/routes/patient/appointments/AppointmentsPage'));
const AppointmentDetailPage = lazy(() => import('@/routes/patient/appointments/AppointmentDetailPage'));
const DocumentsPage = lazy(() => import('@/routes/patient/documents/DocumentsPage'));
// Issue #83 : sans écran, le périmètre de droits par lien était inutilisable — un proche ne
// pouvait rien faire puisqu'aucun droit ne pouvait être accordé.
const RepresentativesPage = lazy(
  () => import('@/routes/patient/representatives/RepresentativesPage').then(m => ({
    default: m.RepresentativesPage,
  }))
);
const AppointmentDocumentsPage = lazy(
  () => import('@/routes/patient/documents/AppointmentDocumentsPage')
);
const TeleconsultationPage = lazy(
  () => import('@/routes/patient/teleconsultation/TeleconsultationPage')
);
const PaymentPage = lazy(() => import('@/routes/patient/payment/PaymentPage'));
const PractitionerDashboardPage = lazy(() => import('@/routes/practitioner/DashboardPage'));
const AvailabilityConfigurationPage = lazy(() => import('@/routes/practitioner/AvailabilityConfigurationPage'));
const PlanningPage = lazy(() => import('@/routes/practitioner/PlanningPage'));
const CenterViewPage = lazy(() => import('@/routes/secretary/CenterViewPage'));
const CenterAgendaPage = lazy(() => import('@/routes/secretary/CenterAgendaPage'));
const DispatchQueuePage = lazy(() => import('@/routes/secretary/DispatchQueuePage'));
const DelegationsPage = lazy(() => import('@/routes/secretary/DelegationsPage'));
const DispatchFilePage = lazy(() => import('@/routes/secretary/DispatchFilePage'));
const BulkActionsPage = lazy(() => import('@/routes/secretary/BulkActionsPage'));
const LegalPage = lazy(() => import('@/routes/legal/LegalPage'));
const NotFoundPage = lazy(() => import('@/routes/NotFoundPage'));
const UnauthorizedPage = lazy(() => import('@/routes/UnauthorizedPage'));

// Loading fallback component
function RouteLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <LoadingState fullScreen message="Chargement..." />
    </div>
  );
}

// Per-role navigation links (B7 — barre de navigation adaptée au rôle)
interface NavEntry {
  to: string;
  label: string;
}

const PATIENT_NAV: NavEntry[] = [
  { to: '/appointments', label: 'Mes rendez-vous' },
  // Issue #54 : sans entree de navigation, la liste d'attente restait inatteignable
  // meme une fois l'ecran construit.
  { to: '/waitlist', label: 'Liste d’attente' },
  { to: '/documents', label: 'Mes documents' },
  { to: '/representatives', label: 'Mes proches' },
  { to: '/search', label: 'Trouver un praticien' },
];

const PRACTITIONER_NAV: NavEntry[] = [
  { to: '/practitioner/dashboard', label: 'Mon planning' },
  { to: '/practitioner/planning', label: 'Planning' },
  { to: '/practitioner/availability', label: 'Configuration' },
];

const SECRETARY_NAV: NavEntry[] = [
  { to: '/secretary', label: 'Centre' },
  { to: '/secretary/agenda', label: 'Agenda du centre' },
  { to: '/secretary/dispatch', label: 'File de replacage' },
];

/**
 * Navigation d'administration (issue #68).
 *
 * <p>Sans entree de navigation, un ecran construit reste inatteignable — c'est le piege deja
 * rencontre avec la liste d'attente (#54). Les trois ecrans prevus par le ticket sont declares ici,
 * meme si un seul existe : mieux vaut une entree qui mene a un ecran « en construction » qu'un ecran
 * que personne ne trouve.
 */
const ADMIN_NAV: NavEntry[] = [
  { to: '/admin/tableau-de-bord', label: 'Tableau de bord' },
  { to: '/admin/praticiens', label: 'Praticiens' },
  { to: '/admin/audit', label: 'Journal d’audit' },
  { to: '/admin/support', label: 'Support' },
];

function roleNav(role: string | undefined | null): NavEntry[] {
  switch (role) {
    case 'PATIENT':
    case 'PATIENT_REPRESENTATIVE':
      return PATIENT_NAV;
    case 'PRACTITIONER_ACTIVE':
    case 'PRACTITIONER_PENDING':
      return PRACTITIONER_NAV;
    case 'MEDICAL_SECRETARY':
      return SECRETARY_NAV;
    case 'ADMIN':
    case 'COMPLIANCE':
    case 'SUPPORT':
      return ADMIN_NAV;
    default:
      return [];
  }
}

// Header with role-aware navigation and session-aware auth actions
function AppHeader() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const nav = isAuthenticated ? roleNav(user?.role) : [{ to: '/search', label: 'Trouver un praticien' }];

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        {/*
          Le logo est le retour a l'accueil conventionnel de toute application web.
          Il n'etait qu'un <div> : sur mobile, ou la barre de navigation est masquee
          (`hidden md:flex`), il ne restait AUCUN moyen de revenir a l'accueil depuis
          un ecran interne — le patient etait enferme dans le tunnel (signale par
          Yacoub : « impossible de revenir sur l'ecran d'accueil »).
        */}
        <Link
          to="/"
          aria-label="ZiMed — retour à l’accueil"
          data-testid="header-home-link"
          className="flex items-center gap-2 shrink-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/40"
        >
          {/*
            Le carre du logo contient un « Z » DESSINE (SVG), pas du texte.
            Un <span>Z</span> etait du texte visible absent du nom accessible
            (« ZiMed — retour a l'accueil ») : axe-core signalait
            `label-content-name-mismatch` (WCAG 2.5.3) sur tous les ecrans, et
            `aria-hidden` sur le conteneur ne suffisait pas — la regle porte sur
            le texte VISIBLE, pas sur l'arbre d'accessibilite. Un glyphe SVG
            decoratif n'est pas du texte : le seul texte visible du lien est
            « ZiMed », qui figure bien dans le nom accessible.
          */}
          <div
            aria-hidden="true"
            className="w-10 h-10 bg-primary-500 rounded-lg flex items-center justify-center"
          >
            <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" aria-hidden="true">
              <path
                d="M6 5h12L6 19h12"
                stroke="white"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <span className="text-xl font-display font-semibold text-gray-900">ZiMed</span>
        </Link>

        {/* Navigation selon le rôle — visible aussi sur mobile, en bandeau scrollable */}
        <nav className="flex items-center gap-4 overflow-x-auto whitespace-nowrap text-sm md:gap-6 md:text-base">
          {nav.map((entry) => (
            <Link
              key={entry.to}
              to={entry.to}
              className="text-gray-600 hover:text-primary-600 transition-colors shrink-0"
            >
              {entry.label}
            </Link>
          ))}
        </nav>

        {/* Auth actions : session active → nom + déconnexion, sinon login/register */}
        <div className="flex items-center gap-4">
          {isAuthenticated && user ? (
            <>
              <span
                data-testid="header-user-name"
                className="text-sm font-medium text-gray-700"
              >
                {user.firstName} {user.lastName}
              </span>
              <button
                type="button"
                onClick={handleLogout}
                className="text-sm text-gray-600 hover:text-error font-medium"
              >
                Déconnexion
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-gray-600 hover:text-primary-600 text-sm font-medium">
                Connexion
              </Link>
              <Link
                to="/register"
                className="bg-primary-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors"
              >
                S'inscrire
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

// Root Layout with header and footer
function RootLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader />

      {/* Main content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 px-6 py-8">
        <div className="max-w-7xl mx-auto text-center text-sm text-gray-500">
          <p>&copy; 2026 ZiMed. Tous droits réservés.</p>
          <div className="flex items-center justify-center gap-4 mt-4">
            <Link to="/privacy" className="hover:text-gray-700">Politique de confidentialité</Link>
            <Link to="/terms" className="hover:text-gray-700">Conditions d'utilisation</Link>
            <Link to="/contact" className="hover:text-gray-700">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Minimal layout for auth pages (no header/footer)
function AuthLayout({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

/** Redirige / et /login vers l'accueil du rôle connecté (B7). */
function RoleHomeRedirect({ anonymousFallback }: { anonymousFallback: string }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  if (!isAuthenticated) {
    return <Navigate to={anonymousFallback} replace />;
  }
  return <Navigate to={roleHomePath(user?.role)} replace />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <CurrentUserProvider>
          <Suspense fallback={<RouteLoader />}>
            <Routes>
              {/* Accueil : redirige vers l'espace du rôle connecté */}
              <Route path="/" element={<RoleHomeRedirect anonymousFallback="/search" />} />

              <Route path="/search" element={<RootLayout><SearchPage /></RootLayout>} />
              {/* La fiche praticien ouvre le tunnel : elle est encapsulee dans BookingProvider (B3). */}
              <Route
                path="/doctors/:doctorId"
                element={
                  <RootLayout>
                    <BookingProvider>
                      <DoctorDetailPage />
                    </BookingProvider>
                  </RootLayout>
                }
              />

              {/* Auth routes with minimal layout */}
              <Route path="/login" element={<AuthLayout><LoginPage /></AuthLayout>} />
              <Route path="/register" element={<AuthLayout><RegisterPage /></AuthLayout>} />
              <Route path="/verify-email" element={<AuthLayout><EmailVerificationPage /></AuthLayout>} />
              <Route path="/forgot-password" element={<AuthLayout><ForgotPasswordPage /></AuthLayout>} />
              <Route path="/reset-password" element={<AuthLayout><ResetPasswordPage /></AuthLayout>} />

              {/* Booking workflow (public but requires auth to complete) */}
              <Route path="/booking/*" element={<RootLayout><BookingPage /></RootLayout>} />

              {/* Protected patient routes */}
              <Route
                path="/appointments"
                element={
                  <RootLayout>
                    <AuthGuard>
                      <PatientGuard>
                        <AppointmentsPage />
                      </PatientGuard>
                    </AuthGuard>
                  </RootLayout>
                }
              />
              <Route
                path="/appointments/:id"
                element={
                  <RootLayout>
                    <AuthGuard>
                      <PatientGuard>
                        <AppointmentDetailPage />
                      </PatientGuard>
                    </AuthGuard>
                  </RootLayout>
                }
              />
              {/* Issue #54 : la liste d'attente patient n'avait aucun ecran ni route. */}
              <Route
                path="/waitlist"
                element={
                  <RootLayout>
                    <AuthGuard>
                      <PatientGuard>
                        <WaitlistPage />
                      </PatientGuard>
                    </AuthGuard>
                  </RootLayout>
                }
              />
              <Route
                path="/documents"
                element={
                  <RootLayout>
                    <AuthGuard>
                      <PatientGuard>
                        <DocumentsPage />
                      </PatientGuard>
                    </AuthGuard>
                  </RootLayout>
                }
              />
              <Route
                path="/representatives"
                element={
                  <RootLayout>
                    <AuthGuard>
                      <PatientGuard>
                        <RepresentativesPage />
                      </PatientGuard>
                    </AuthGuard>
                  </RootLayout>
                }
              />

              {/* Téléconsultation : patient ET praticien (issue #27) */}
              <Route
                path="/appointments/:appointmentId/teleconsultation"
                element={
                  <AuthGuard>
                    <RoleGuard
                      allowedRoles={[
                        'PATIENT',
                        'PATIENT_REPRESENTATIVE',
                        'PRACTITIONER_ACTIVE',
                      ]}
                    >
                      <TeleconsultationPage />
                    </RoleGuard>
                  </AuthGuard>
                }
              />

              {/* Paiement en ligne (mode démonstration) */}
              <Route
                path="/appointments/:appointmentId/payment"
                element={
                  <RootLayout>
                    <AuthGuard>
                      <PatientGuard>
                        <PaymentPage />
                      </PatientGuard>
                    </AuthGuard>
                  </RootLayout>
                }
              />

              {/* Documents d'un rendez-vous précis */}
              <Route
                path="/appointments/:appointmentId/documents"
                element={
                  <RootLayout>
                    <AuthGuard>
                      <RoleGuard
                        allowedRoles={[
                          'PATIENT',
                          'PATIENT_REPRESENTATIVE',
                          'PRACTITIONER_ACTIVE',
                        ]}
                      >
                        <AppointmentDocumentsPage />
                      </RoleGuard>
                    </AuthGuard>
                  </RootLayout>
                }
              />

              {/* Practitioner routes (/practitioner nu redirige, jamais 404 — B7) */}
              <Route
                path="/practitioner"
                element={
                  <AuthGuard>
                    <PractitionerGuard>
                      <Navigate to="/practitioner/dashboard" replace />
                    </PractitionerGuard>
                  </AuthGuard>
                }
              />
              <Route
                path="/practitioner/dashboard"
                element={
                  <RootLayout>
                    <AuthGuard>
                      <PractitionerGuard>
                        <PractitionerDashboardPage />
                      </PractitionerGuard>
                    </AuthGuard>
                  </RootLayout>
                }
              />
              <Route
                path="/practitioner/availability"
                element={
                  <RootLayout>
                    <AuthGuard>
                      <PractitionerGuard>
                        <AvailabilityConfigurationPage />
                      </PractitionerGuard>
                    </AuthGuard>
                  </RootLayout>
                }
              />
              <Route
                path="/practitioner/planning"
                element={
                  <RootLayout>
                    <AuthGuard>
                      <PractitionerGuard>
                        <PlanningPage />
                      </PractitionerGuard>
                    </AuthGuard>
                  </RootLayout>
                }
              />

              {/* Secretary routes */}
              <Route
                path="/secretary"
                element={
                  <RootLayout>
                    <AuthGuard>
                      <SecretaryGuard>
                        <CenterViewPage />
                      </SecretaryGuard>
                    </AuthGuard>
                  </RootLayout>
                }
              />
              <Route
                path="/secretary/agenda"
                element={
                  <RootLayout>
                    <AuthGuard>
                      <SecretaryGuard>
                        <CenterAgendaPage />
                      </SecretaryGuard>
                    </AuthGuard>
                  </RootLayout>
                }
              />
              <Route
                path="/secretary/dispatch"
                element={
                  <RootLayout>
                    <AuthGuard>
                      <SecretaryGuard>
                        <DispatchQueuePage />
                      </SecretaryGuard>
                    </AuthGuard>
                  </RootLayout>
                }
              />
              <Route
                path="/secretary/bulk-actions"
                element={
                  <RootLayout>
                    <AuthGuard>
                      <SecretaryGuard>
                        <BulkActionsPage />
                      </SecretaryGuard>
                    </AuthGuard>
                  </RootLayout>
                }
              />
              <Route
                path="/secretary/dispatch-file"
                element={
                  <RootLayout>
                    <AuthGuard>
                      <SecretaryGuard>
                        <DispatchFilePage />
                      </SecretaryGuard>
                    </AuthGuard>
                  </RootLayout>
                }
              />
              <Route
                path="/secretary/delegations"
                element={
                  <RootLayout>
                    <AuthGuard>
                      <SecretaryGuard>
                        <DelegationsPage />
                      </SecretaryGuard>
                    </AuthGuard>
                  </RootLayout>
                }
              />

              {/* Administration — issue #68.
                  Protege par AdminGuard : un patient qui tape l'URL est renvoye vers /unauthorized.
                  La protection serveur existe aussi (verifyAdminPermission) : le guard front n'est
                  qu'un confort d'usage, pas la securite. */}
              <Route
                path="/admin/praticiens"
                element={
                  <RootLayout>
                    <AdminGuard>
                      <AdminPractitionersPage />
                    </AdminGuard>
                  </RootLayout>
                }
              />

              <Route
                path="/admin/audit"
                element={
                  <RootLayout>
                    <AdminGuard>
                      <AdminAuditPage />
                    </AdminGuard>
                  </RootLayout>
                }
              />

              <Route
                path="/admin/support"
                element={
                  <RootLayout>
                    <AdminGuard>
                      <AdminSupportPage />
                    </AdminGuard>
                  </RootLayout>
                }
              />

              <Route
                path="/admin/tableau-de-bord"
                element={
                  <RootLayout>
                    <AdminGuard>
                      <AdminDashboardPage />
                    </AdminGuard>
                  </RootLayout>
                }
              />

              {/* Pages légales — issue #61. Les liens historiques /terms et /privacy
                  sont conservés pour ne pas casser les liens existants. */}
              <Route path="/terms" element={<LegalPage kind="terms" />} />
              <Route path="/cgu" element={<LegalPage kind="terms" />} />
              <Route path="/privacy" element={<LegalPage kind="privacy" />} />
              <Route path="/confidentialite" element={<LegalPage kind="privacy" />} />
              <Route path="/contact" element={<LegalPage kind="contact" />} />

              {/* Error pages */}
              <Route path="/unauthorized" element={<UnauthorizedPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </CurrentUserProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
